import React, { useContext } from 'react';

import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    LinearProgress,
    Tooltip,
    TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const SearchResults = (props) => {
    const { searchResults, loadingSearch, ts, isSmallScreen, paymentsArray, payments, handleSubmit, entity, handlePaymentChange, paymentData, noRowsText, setNoRowsText } = props;

    function newIntlNumberFormat(params) {
        return (new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(params?.value))
    }
    // table/data-grid columns
    const columns = [
        {
            field: 'currentStatute',
            headerName: 'Statute',
            flex: 1,
            renderHeader: (params) => isSmallScreen ? null : params.colDef.headerName,
        },
        {
            field: 'description',
            headerName: "Charges",
            headerClassName: 'hideRightSeparator',
            sortable: !isSmallScreen,
            flex: 1
        },
        {
            field: 'totalFines',
            headerName: 'Fines',
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => newIntlNumberFormat(params),
            renderHeader: (params) => isSmallScreen ? null : params.colDef.headerName,
        },
        {
            field: 'totalFees',
            headerName: 'Fees',
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => newIntlNumberFormat(params),
            renderHeader: (params) => isSmallScreen ? null : params.colDef.headerName,
        },
        {
            field: 'totalBalance',
            headerName: 'Total Due',
            align: 'right',
            headerAlign: 'right',
            flex: 1,
            renderCell: (params) => newIntlNumberFormat(params),
            renderHeader: (params) => isSmallScreen ? null : params.colDef.headerName,
            headerClassName: 'hideRightSeparator',
            sortable: !isSmallScreen
        },
        {
            field: 'payment',
            headerName: 'Payment Today',
            flex: 1,
            headerAlign: 'right',
            align: 'right',
            renderCell: (params) => {
                const row = params?.row;
                const paymentValue = (payments && payments[row?.id]) || '';

                return (
                    <Tooltip title={params.row.totalBalance === 0.00 && "NO CURRENT PAYMENT DUE ON THIS VIOLATION"}>
                        <TextField
                            sx={ts}
                            id={`payment-${row?.id}`}
                            placeholder="####.##"
                            value={paymentValue}
                            onChange={(e) => handlePaymentChange(e, row)}
                            disabled={params.row.totalBalance === 0.00}
                        />
                    </Tooltip>
                );
            },
            renderHeader: (params) => isSmallScreen ? null : params.colDef.headerName,
            headerClassName: 'hideRightSeparator',
            sortable: !isSmallScreen,
        },
    ];

    const customLocaleText = `Online Payments are not allowed on this case. Payments can only be made in person at\n${entity?.entityName} located at: ${entity?.streetNumber} ${entity?.streetName}, ${entity?.city}, ${entity?.state} ${entity?.zip}.\nPlease contact the Sheriff's office at ${entity?.phone} with any questions or concerns regarding this matter.`

    const onlinePaymentsNotAllowedMessage = (
        <>
            {customLocaleText.split("\n").map((line, index) => (
                <React.Fragment key={index}>
                    {line}
                    <br />
                </React.Fragment>
            ))}
        </>
    );

    const disablePaymentsValidation = searchResults?.some(a => a.isDataMisMatch || a.isCasePaymentLocked || a.caseStatus.toUpperCase() === 'HOLD' || a.issuedChargeBack || a.isCreditCardPaymentsLocked)

    const localeText = {
        noRowsLabel: noRowsText.length > 0 ? noRowsText : loadingSearch ? "SEARCHING FOR YOUR VIOLATIONS" ? disablePaymentsValidation : onlinePaymentsNotAllowedMessage : "No Rows Available",
    };

    return (
        <Card sx={{ width: isSmallScreen ? '99%' : '55%', m: '.5vh auto' }} elevation={5}>
            <CardHeader title="Violations With Outstanding Balances" />
            <CardContent sx={{ margin: isSmallScreen ? '0 0' : null, p: '0', pl: '.2' }}>
                {isSmallScreen ? <Box sx={{ height: "35vh", overFlow: "auto", width: 'auto', mx: '1vw' }}>
                    <DataGrid
                        rows={disablePaymentsValidation ? [] : searchResults.filter(sr => sr.totalBalance > 0.00)}
                        columns={columns.filter(col => col.field === 'payment' || col.field === 'totalBalance' || col.field === 'description').map(c => c)}
                        loading={loadingSearch}
                        slots={{ loadingOverlay: LinearProgress }}
                        localeText={localeText}
                        disableRowSelectionOnClick
                        hideHeader={columns.find(col => col.field !== 'description')}
                        disableColumnMenu={true}  // Disable column menu for all columns
                        disableColumnResize={true}
                        disableSort={true}
                        sx={{
                            ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
                                marginTop: "1em",
                                marginBotton: "1em"
                            },
                            "& .MuiDataGrid-overlay": {
                                fontSize: "1.3rem"
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                //borderBottom: 'none',
                                borderLeft: 'none',// Remove the bottom border of the column headers
                                borderRight: 'none',// Remove the bottom border of the column headers
                            },
                            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                                borderRight: 'none',  // No vertical borders between columns
                            },
                            '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
                                display: 'none',
                            }, '& .MuiDataGrid-cell--focus': {
                                outline: 'none', // Remove focus outline from cells
                            },
                            '& .MuiDataGrid-columnHeader--focus': {
                                outline: 'none', // Remove focus outline from column headers
                            },
                            '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
                                ':focus': {
                                    outline: 'none', // Ensure no focus outline is shown on focus
                                },
                            },
                        }}
                    />
                </Box> : <Box sx={{ height: "35vh" }}>
                    <DataGrid
                        rows={disablePaymentsValidation ? [] : searchResults.filter(sr => sr.totalBalance > 0.00)}
                        columns={columns}
                        loading={loadingSearch}
                        localeText={localeText}
                        disableRowSelectionOnClick
                        sx={{
                            ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
                                marginTop: "1em",
                                marginBotton: "1em"
                            },
                            "& .MuiDataGrid-overlay": {
                                fontSize: "1.3rem"
                            }, '& .MuiDataGrid-cell--focus': {
                                outline: 'none', // Remove focus outline from cells
                            },
                            '& .MuiDataGrid-columnHeader--focus': {
                                outline: 'none', // Remove focus outline from column headers
                            },
                            '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
                                ':focus': {
                                    outline: 'none', // Ensure no focus outline is shown on focus
                                },
                            },// First row padding
                            '& .MuiDataGrid-row:nth-of-type(1) .MuiDataGrid-cell': {
                                paddingLeft: 5,
                            },// First row padding
                            '& .MuiDataGrid-row:nth-of-type(2) .MuiDataGrid-cell': {
                                paddingLeft: 5,
                            },
                            // Last row padding
                            '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
                                paddingLeft: 5,
                            },
                            // First column padding
                            '& .MuiDataGrid-cell:first-of-type': {
                                paddingLeft: 2,
                            },
                            // Last column padding
                            '& .MuiDataGrid-cell:last-of-type': {
                                paddingRight: 2,
                            },
                            // First column header padding
                            '& .MuiDataGrid-columnHeader:first-of-type': {
                                paddingLeft: 5,
                            },
                            // Last column header padding
                            '& .MuiDataGrid-columnHeader:last-of-type': {
                                paddingRight: 2,
                            },
                        }}
                    />
                </Box>}
            </CardContent>
            <CardActions sx={{ display: 'flex', justifyContent: 'end', pb: '1vh' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSubmit()}
                    disabled={(paymentData && Object.values(paymentData)?.every(payment => payment?.payment === '0.00')) || disablePaymentsValidation || searchResults?.length < 1 || !paymentsArray?.length}
                    sx={{ backgroundColor: 'steelblue', color: 'white', height: isSmallScreen ? '7.5vh' : null, width: isSmallScreen ? '100%' : null }}
                >
                    <strong>Submit Payments</strong>
                </Button>
            </CardActions>

        </Card>
    )
}

export default SearchResults;